import { Controller } from "@hotwired/stimulus"
import { OUTBOX_CHANGED_EVENT } from "../native_outbox"
import { intlLocale } from "../lib/intl_locale"
import { interpolate } from "../lib/interpolate"

// Inserted by the service worker into the stored copy it serves.
const OFFLINE_COPY_META = 'meta[name="offline-copy"]'

// Copy mode on the home (spec §3.4). The service worker inserts
// <meta name="offline-copy"> into a stored copy it serves, so the page knows
// before any script runs. In copy mode the page shows the offline banner with
// the copy's age and disables the File tab and every form that posts: they
// would post with the copy's CSRF token, which can belong to an expired
// session. The link and text tabs go to the native outbox (outbox_controller.js).
//
// When the app reports a change (connection back, items sent), the page loads
// "/" again. A live answer ends copy mode. While the compose form has input or
// focus, only the history list is replaced, so typing is never erased.
//
// Rendered only for an app that announces Outbox/1.
export default class extends Controller {
  static targets = [ "banner" ]
  // bannerTemplate holds "%{ago}", replaced with the age of the copy.
  static values = { bannerTemplate: String, bannerJustNow: String, locale: String }

  connect() {
    this.onChanged = this.changed.bind(this)
    document.addEventListener(OUTBOX_CHANGED_EVENT, this.onChanged)
    // The tabs controller may connect after this one and restore a saved
    // File tab; it announces that with tabs:restored.
    this.onTabsRestored = () => { if (this.isCopy) this.leaveFileTab() }
    this.element.addEventListener("tabs:restored", this.onTabsRestored)
    if (this.isCopy) this.enterCopyMode()
  }

  disconnect() {
    document.removeEventListener(OUTBOX_CHANGED_EVENT, this.onChanged)
    this.element.removeEventListener("tabs:restored", this.onTabsRestored)
    clearInterval(this.bannerTimer)
  }

  get isCopy() {
    return Boolean(document.head.querySelector(OFFLINE_COPY_META))
  }

  changed(event) {
    if (this.isCopy || event.detail?.sent > 0) this.refresh()
  }

  enterCopyMode() {
    this.showBanner()
    this.bannerTimer = setInterval(() => this.showBanner(), 60_000)

    this.leaveFileTab()
    const fileTab = this.fileTab
    if (fileTab) this.disable(fileTab)
    document.querySelectorAll("form").forEach((form) => {
      if (form.method === "post" && !this.isComposeForm(form)) this.disable(form)
    })
    document.querySelectorAll("a[data-turbo-method]").forEach((link) => this.disable(link))
  }

  get fileTab() {
    return this.element.querySelector('[data-upload-tab="file"]')
  }

  // Files cannot be queued: move off the File tab before it is disabled.
  leaveFileTab() {
    const fileTab = this.fileTab
    if (fileTab?.getAttribute("aria-selected") !== "true") return

    this.element.querySelector('#upload-tabs [role="tab"]')?.click()
  }

  leaveCopyMode() {
    clearInterval(this.bannerTimer)
    this.bannerTarget.classList.add("hidden")
    document.head.querySelector(OFFLINE_COPY_META)?.remove()
    document.querySelectorAll("[data-offline-disabled]").forEach((element) => {
      element.inert = false
      element.classList.remove("opacity-50")
      element.removeAttribute("aria-disabled")
      element.removeAttribute("data-offline-disabled")
      if (element.matches("button")) element.disabled = false
    })
  }

  disable(element) {
    element.inert = true
    element.classList.add("opacity-50")
    element.setAttribute("aria-disabled", "true")
    element.setAttribute("data-offline-disabled", "")
    if (element.matches("button")) element.disabled = true
  }

  showBanner() {
    const renderedAt = Date.parse(document.head.querySelector('meta[name="rendered-at"]')?.content || "")
    this.bannerTarget.textContent = this.bannerText(Number.isNaN(renderedAt) ? null : renderedAt)
    this.bannerTarget.classList.remove("hidden")
  }

  bannerText(renderedAt) {
    const minutes = renderedAt ? Math.floor((Date.now() - renderedAt) / 60_000) : 0
    if (minutes < 1) return this.bannerJustNowValue

    const format = new Intl.RelativeTimeFormat(intlLocale(this.localeValue), { numeric: "always" })
    let ago
    if (minutes < 60) ago = format.format(-minutes, "minute")
    else if (minutes < 60 * 24) ago = format.format(-Math.floor(minutes / 60), "hour")
    else ago = format.format(-Math.floor(minutes / (60 * 24)), "day")
    return interpolate(this.bannerTemplateValue, { ago })
  }

  async refresh() {
    if (this.refreshing) return
    this.refreshing = true

    try {
      const response = await fetch("/", { headers: { Accept: "text/html" }, credentials: "same-origin" })
      if (!response.ok) return

      const html = await response.text()
      const page = new DOMParser().parseFromString(html, "text/html")
      if (page.head.querySelector(OFFLINE_COPY_META)) return // Still offline.

      if (response.redirected || !this.isComposing) {
        // A full visit also reconnects the Turbo Stream subscriptions, and
        // follows the redirect to sign-in when the session has expired.
        window.Turbo?.visit("/", { action: "replace" })
      } else {
        this.replaceHistory(page)
      }
    } catch {
      // Still offline: the copy stays until the next change.
    } finally {
      this.refreshing = false
    }
  }

  replaceHistory(page) {
    const list = document.getElementById("uploads-list")
    const fresh = page.getElementById("uploads-list")
    if (!list || !fresh) return

    // The Turbo Stream sources stay from the copy; Action Cable reconnects them by itself.
    list.innerHTML = fresh.innerHTML
    for (const name of [ "csrf-param", "csrf-token", "rendered-at" ]) {
      const current = document.head.querySelector(`meta[name="${name}"]`)
      const next = page.head.querySelector(`meta[name="${name}"]`)
      if (current && next) current.content = next.content
    }
    if (this.isCopy) this.leaveCopyMode()
    this.dispatch("refreshed")
  }

  get isComposing() {
    const form = this.composeForm
    if (!form) return false
    if (form.contains(document.activeElement)) return true

    return [ ...form.querySelectorAll('input[type="url"], input[type="text"], textarea') ]
      .some((field) => field.value.trim() !== "")
  }

  get composeForm() {
    return this.element.querySelector('form[data-controller~="upload-form"]')
  }

  isComposeForm(form) {
    return form === this.composeForm
  }
}
